/**
 * @fileoverview Modal component for displaying detailed company association information
 * @active This component is actively used in the person details view
 *
 * @see {@link src/components/personBy_Id/overview/AssociatedCompanies/AssociatedCompanies.tsx}
 * @see {@link src/utils/addressSelector.ts}
 */

import {
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import Link from "next/link";

import CloseModalSvg from "@/assets/svg/close-modal.svg";
import { urlChecker } from "@/components/ui/buttons/ExternalUrlIcons.server";
import DefaultImage from "@/components/ui/DefaultImage";
import { h_400_12_135_48, h_700_16_100_32 } from "@/styles/fontStyles";
import { addressSelector } from "@/utils/addressSelector";
import { imgSelector } from "@/utils/imgSelector";

import {
  LinkedInWebSiteElement,
  PlainTextInTable,
} from "./AssociatedCompanies";

/**
 * Modal component for displaying detailed company association information
 * @param {Object} props - Component props
 * @param {Object} props.control - Modal control object containing isOpen and onClose
 * @param {Object} props.data - Company association data to display
 * @returns {JSX.Element} Modal component
 */
export const AssociatedCompaniesModal = ({ control, data }) => {
  const { isOpen, onClose } = control;

  const renderData = [
    {
      title: "HQ",
      value: addressSelector({ data: data?.resEntity?.addresses }),
      width: "156px",
    },
    {
      title: "Website",
      value: urlChecker(data?.resEntity?.webUrl?.urlWebsite),
      width: "48px",
    },
    {
      title: "LinkedIn",
      value: urlChecker(data?.resEntity?.webUrl?.urlLinkedin),
      width: "49px",
    },
    {
      title: "Title",
      value: data?.corporateTitle?.personCompanyTitle,
      width: "auto",
    },
    {
      title: "Level",
      value: data?.corporateTitle?.personEntityJobLevel,
      width: "auto",
    },
    {
      title: "Function",
      value: data?.corporateTitle?.personEntityFunction,
      width: "auto",
    },
  ];

  /**
   * Renders different block types based on the item type
   * @param {Object} params - Block switch parameters
   * @param {Object} params.item - Item to render
   * @returns {JSX.Element} Rendered block component
   */
  const blockSwitcher = ({ item }) => {
    switch (item?.title) {
      case "HQ":
        return <PlainTextInTable {...{ data: item?.value || "—" }} />;
      case "Website":
        return item?.value ? (
          <LinkedInWebSiteElement {...{ url: item?.value, type: "webSite" }} />
        ) : (
          <PlainTextInTable />
        );

      case "LinkedIn":
        return item?.value ? (
          <LinkedInWebSiteElement {...{ url: item?.value, type: "linkedIn" }} />
        ) : (
          <PlainTextInTable />
        );
      default:
        return (
          <PlainTextInTable
            {...{ data: item?.value, styleText: { color: "mainBlue" } }}
          />
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent w={"327px"} p={"10px 20px 20px 20px"}>
        <ModalBody p={"0px"}>
          <Flex flexDir={"column"} gap={"16px"}>
            <Flex justify={"space-between"} align={"center"}>
              <Flex gap={"8px"} align={"center"}>
                <DefaultImage
                  src={imgSelector({ data: data?.resEntity })}
                  options={{
                    blockSize: "24px",
                    borderRadius: "2px",
                    objectFit: "scale-down",
                  }}
                />
                <Link href={`/companies/${data?.resEntity?.slug}`}>
                  <Tooltip
                    label={data?.resEntity?.nameBrand || ""}
                    placement="top"
                    borderRadius={"4px"}
                    pt={"4px"}
                  >
                    <Text
                      {...h_700_16_100_32}
                      color={"naturalBlack"}
                      pt={"4px"}
                      maxW={"85%"}
                      isTruncated
                    >
                      {data?.resEntity?.nameBrand}
                    </Text>
                  </Tooltip>
                </Link>
              </Flex>
              <Flex
                align={"center"}
                onClick={() => {
                  onClose();
                }}
                cursor={"pointer"}
              >
                <CloseModalSvg />
              </Flex>
            </Flex>
            <Flex flexWrap={"wrap"} justify={"space-between"} gap={"8px"}>
              {data &&
                renderData?.map((item, index) => {
                  return (
                    <Flex
                      flexDir={"column"}
                      key={`${item?.title}-${index}`}
                      w={item?.width}
                      gap={"4px"}
                    >
                      <Text {...h_400_12_135_48} color={"gray"}>
                        {item?.title}
                      </Text>
                      {blockSwitcher({ item })}
                    </Flex>
                  );
                })}
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
